// src/components/Navbar.tsx

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { Popover } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory, faKey, faSignOut, faUser } from '@fortawesome/free-solid-svg-icons';
import { usePathname } from 'next/navigation';
import { signOut } from 'next-auth/react';
import { useUserProfile } from '@/services/UserProfileService';
import nProgress from 'nprogress';
import AccelistGroupDropdown from './AccelistGroupDropdown';
import clearLocalStorage from '@/functions/clearLocalStorage';

interface NavbarProps {
  items: { href: string, label: string }[];
}

export default function Navbar({ items }: NavbarProps) {
  const { data: session, status } = useSession();
  const canChangePassword = session ? session['canChangePassword'] : false;
  const isEmailVerified = session ? session['email_verified'] : false;
  const pathname = usePathname();
  const [displayUserName, setDisplayUserName] = useState('');
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const userId = session?.user?.["id"];
  const { userProfile } = useUserProfile(userId);

  useEffect(() => {
    if (userProfile) {
      const finalFullName = userProfile.fullName.replace(/\r?\n|\r/g, "");
      setDisplayUserName(finalFullName.length > 20 ? finalFullName.substring(0, 20) + "..." : finalFullName);
    } else if (session?.user?.name) {
      const finalSessionName = session.user.name.replace(/\r?\n|\r/g, "");
      setDisplayUserName(finalSessionName.length > 20 ? finalSessionName.substring(0, 20) + "..." : finalSessionName);
    } else {
      setDisplayUserName('');
    }

    return () => {
      clearLocalStorage();
    };
  }, [userProfile, session]);

  const handleLogout = () => {
    if (status === 'authenticated') {
      nProgress.start();
      signOut({
        callbackUrl: '/',
        redirect: true,
      }).then(() => {
        nProgress.done();
      });
    }
  };

  const popoverContent = () => {
    return (
      <div className="text-slate-500">
        { isEmailVerified && <>
        <div className="flex text-center items-center mb-2 mx-2">
          <FontAwesomeIcon icon={faUser} color="gray" />
          <Link
            href="/profileDetail"
            className="text-gray-500 hover:text-gray-700"
            onClick={() => {
              setPopoverVisible(false);
              setMenuOpen(!menuOpen);
              }}
              >
            <h3 className="pl-3">Profile</h3>
          </Link>
        </div>
        <div className="flex text-center items-center mb-2 mx-2">
          <FontAwesomeIcon icon={faHistory} color="gray" />
          <Link
            href={"/application-history"}
            className="text-gray-500 hover:text-gray-700"
            onClick={() => {
              setPopoverVisible(false);
              setMenuOpen(!menuOpen);
              }}
          >
            <h3 className="pl-3">Application History</h3>
          </Link>
        </div>
        { canChangePassword && <div className="flex text-center items-center mb-2 mx-2">
          <FontAwesomeIcon icon={faKey} color="gray" />
          <Link
            href={`/changePassword`}
            className="text-gray-500 hover:text-gray-700"
            onClick={() => {
              setPopoverVisible(false);
              setMenuOpen(!menuOpen);
              }}
          >
            <h3 className="pl-3">Change Password</h3>
          </Link>
        </div>
        }
        <hr className="my-2 border-gray-300" /> </>}
        <div className="flex text-center items-center mb-2 mx-2 text-gray-500 hover:text-gray-700">
          <FontAwesomeIcon icon={faSignOut} color="gray" />
          <button onClick={handleLogout}>
            <h3 className="pl-3">Sign Out</h3>
          </button>
        </div>
      </div>
    );
  };

  return (
    // <Authorize>
      <nav className="bg-white border-gray-100 ">
        <div className="flex flex-wrap items-center justify-between mx-auto sm:px-5 pl-2 px-0 sm:py-1 py-0">
          <div className="flex flex-wrap items-center">
            <Link
              href="/"
              className="flex items-center sm:space-x-3 space-x-1 rtl:space-x-reverse"
            >
              <img
                src="/accelist-logo.png"
                className="h-10 sm:h-14"
                alt="Accelist Logo"
              />
            </Link>

            <div
              className="block w-auto lg:pl-16 sm:pl-5 pl-0"
              id="navbar-default"
            >
              {/* Add key to <ul> to forces re-render when the key changes and properly shows underline in the menu */}
              <ul
                key={pathname}
                className="font-medium flex p-0 rounded-lg lex-row md:space-x-8 rtl:space-x-reverse mt-0 border-0 bg-white"
              >
                {items.map(({ href, label }) => {
                  const isActive = pathname === href;
                  return (
                    <li key={label}>
                      {label == "Accelist Group" ?  
                        <AccelistGroupDropdown isActive={isActive}/>
                       : <Link
                            href={href}
                            className={`block py-2 sm:px-3 px-2 rounded hover:bg-primary hover:bg-transparent hover:text-primary md:p-0 hover:underline hover:decoration-2 hover:underline-offset-4 decoration-primary ${
                              isActive
                                ? "text-primary underline decoration-2 underline-offset-4 sm:text-sm text-xs"
                                : "text-gray-400 sm:text-sm text-xs"
                            }`}
                          >
                        {label}
                      </Link>
                    }
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
          <div className="pr-3 pb-2 sm:pr-0">
            <div
              className="w-full md:block md:w-auto sm:pr-[1.375rem] pr-0"
              id="navbar-default"
            >
              {status === "authenticated" ? (
                <div className="flex flex-row items-center hover:cursor-pointer">
                  <Popover
                    placement="bottom"
                    content={popoverContent}
                    arrow={false}
                    trigger="click"
                    open={popoverVisible}
                    onOpenChange={(visible) => setPopoverVisible(visible)}
                  >
                    <div className="flex pt-2">
                      {userProfile?.profileImageUrl && (
                        <img
                          src={userProfile?.profileImageUrl || "/blank-profile.png"}
                          alt="Profile Picture"
                          className="w-7 h-7 sm:w-10 sm:h-10 rounded-full"
                        />
                      )}
                      <h2 className="font-poppins font-medium text-primary pl-0 sm:pl-2">
                        <div className="sm:block py-1 sm:py-2 px-1 sm:px-3 text-gray-400 text-xs sm:text-sm break-words hidden">
                          {" "}
                          {displayUserName}
                        </div>
                      </h2>
                    </div>
                  </Popover>
                </div>
              ) : (
                <div className="flex flex-row" >
                  <ul className="flex mt-2 rounded-lg flex-row md:space-x-8 rtl:space-x-reverse bg-white ">
                    <li>
                        <Link href="/register"
                          className={`font-medium text-gray-400 block py-2 sm:px-3 px-2 rounded hover:bg-primary hover:bg-transparent md:border-0 hover:text-primary md:p-0 hover:underline hover:decoration-2 hover:underline-offset-4 decoration-primary sm:text-sm text-xs`}
                        >
                          Register
                        </Link>
                    </li>
                    <li>
                    <Link href="/login"
                          className={`font-medium text-gray-400 block py-2 sm:px-3 px-0 rounded hover:bg-primary hover:bg-transparent md:border-0 hover:text-primary md:p-0 hover:underline hover:decoration-2 hover:underline-offset-4 decoration-primary sm:text-sm text-xs`}
                        >
                          Login
                        </Link>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    // {/* </Authorize> */}
  );
}
